import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import { submitTrackingUsage } from './trackingUsageBus';
import './restock.css';

function catalogText(item) {
  return [item?.item, item?.component, item?.catalogId].map(value => String(value || '').toLowerCase()).join(' ');
}

function cleanQuantity(value) {
  const parsed = Number(String(value || '').trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function usageKey(item) {
  return `${String(item?.deploymentId || '').trim()}|${String(item?.assetId || '').trim().toUpperCase()}|${String(item?.catalogId || '').trim()}`;
}

export default function UsedSupplyReporter({ selectedAssets, hasUse, onHasUseChange, value, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [lockedRevision, setLockedRevision] = useState(0);
  const [draft, setDraft] = useState({ assetId: '', query: '', catalogId: '', quantity: '1' });
  const [reviewing, setReviewing] = useState(false);
  const initializedAssetsRef = useRef(new Set());
  const lockedMinimumsRef = useRef(new Map());
  const rootRef = useRef(null);
  const restockConfirmedRef = useRef(false);

  useEffect(() => {
    if (!hasUse) return undefined;
    let cancelled = false;
    setStatus('loading');
    setError('');
    getMemberAccessToken()
      .then(token => {
        if (!token) throw new Error('Member sign in required.');
        return fetch('/api/member-tracking?mode=inventory-catalog', {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
      })
      .then(async response => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || 'Unable to load inventory catalog.');
        if (!cancelled) {
          setCatalog(Array.isArray(payload?.catalog) ? payload.catalog : []);
          setStatus('ready');
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || 'Unable to load inventory catalog.');
          setStatus('error');
        }
      });
    return () => { cancelled = true; };
  }, [hasUse, retryKey]);

  useEffect(() => {
    const ids = new Set((selectedAssets || []).map(item => item.id));
    if (draft.assetId && ids.has(draft.assetId)) return;
    const first = selectedAssets?.[0]?.id || '';
    setDraft(current => ({ ...current, assetId: first }));
  }, [selectedAssets, draft.assetId]);

  useEffect(() => {
    if (hasUse !== true) return;
    let changed = false;
    (selectedAssets || []).forEach(asset => {
      const assetId = String(asset?.id || '').trim().toUpperCase();
      const deploymentId = String(asset?.deploymentId || '').trim();
      const assetKey = `${deploymentId}|${assetId}`;
      if (!assetId || initializedAssetsRef.current.has(assetKey)) return;

      (value || [])
        .filter(item => String(item?.deploymentId || '').trim() === deploymentId && String(item?.assetId || '').trim().toUpperCase() === assetId)
        .forEach(item => {
          const quantity = cleanQuantity(item?.quantity);
          if (quantity > 0) lockedMinimumsRef.current.set(usageKey(item), quantity);
        });
      initializedAssetsRef.current.add(assetKey);
      changed = true;
    });
    if (changed) setLockedRevision(current => current + 1);
  }, [selectedAssets, value, hasUse]);

  // Any change to the restock list invalidates an earlier confirmation.
  useEffect(() => {
    restockConfirmedRef.current = false;
    setReviewing(false);
  }, [hasUse, value]);

  // Intercept the parent Return form before its onSubmit handler. If supplies are being
  // ordered, the member must see the final list and answer the "anything else?" question
  // before the actual restock request / gear return is allowed to continue.
  useEffect(() => {
    const form = rootRef.current?.closest('form');
    if (!form) return undefined;
    const interceptReturn = event => {
      if (hasUse !== true || !(value || []).length || restockConfirmedRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      setReviewing(true);
    };
    form.addEventListener('submit', interceptReturn, true);
    return () => form.removeEventListener('submit', interceptReturn, true);
  }, [hasUse, value]);

  const lockedQuantity = item => Number(lockedMinimumsRef.current.get(usageKey(item)) || 0);
  const hasLockedUsage = lockedRevision >= 0 && (value || []).some(item => lockedQuantity(item) > 0);

  const selectedCatalog = useMemo(
    () => catalog.find(item => item.catalogId === draft.catalogId) || null,
    [catalog, draft.catalogId],
  );

  const matches = useMemo(() => {
    const query = draft.query.trim().toLowerCase();
    if (!query || selectedCatalog) return [];
    return catalog.filter(item => catalogText(item).includes(query)).slice(0, 12);
  }, [catalog, draft.query, selectedCatalog]);

  const chooseCatalog = item => {
    setDraft(current => ({ ...current, catalogId: item.catalogId, query: item.item }));
    setError('');
  };

  const recordUsageAdjustment = (item, quantityDelta) => {
    submitTrackingUsage({
      deploymentId: item.deploymentId,
      assetId: item.assetId,
      assetName: item.assetName,
      catalogId: item.catalogId,
      component: item.component || '',
      item: item.item,
      quantityDelta,
      source: 'return',
    });
  };

  const addItem = () => {
    const asset = selectedAssets.find(item => item.id === draft.assetId);
    const quantity = cleanQuantity(draft.quantity);
    if (!asset) return setError('Choose the bag or container the item came from.');
    if (!selectedCatalog) return setError('Choose an item from the inventory catalog.');
    if (!quantity || !Number.isInteger(quantity)) return setError('Enter a whole-number quantity greater than zero.');

    const nextItem = {
      deploymentId: asset.deploymentId,
      assetId: asset.id,
      assetName: asset.name,
      catalogId: selectedCatalog.catalogId,
      component: selectedCatalog.component || '',
      item: selectedCatalog.item,
      quantity,
    };

    try {
      recordUsageAdjustment(nextItem, quantity);
    } catch (err) {
      return setError(err?.message || 'Unable to save this usage to the deployment history.');
    }

    const existingIndex = value.findIndex(item => item.deploymentId === nextItem.deploymentId && item.assetId === nextItem.assetId && item.catalogId === nextItem.catalogId);
    if (existingIndex >= 0) {
      const next = [...value];
      next[existingIndex] = { ...next[existingIndex], quantity: Number(next[existingIndex].quantity || 0) + quantity };
      onChange(next);
    } else {
      onChange([...value, nextItem]);
    }
    setDraft(current => ({ ...current, query: '', catalogId: '', quantity: '1' }));
    setError('');
  };

  const removeItem = index => {
    const item = value[index];
    if (!item) return;
    const currentQuantity = cleanQuantity(item.quantity);
    const minimum = lockedQuantity(item);
    if (currentQuantity <= minimum) {
      return setError('This item was reported during the deployment. Use Undo in My Gear before returning if that field report is incorrect.');
    }

    const removable = currentQuantity - minimum;
    try {
      recordUsageAdjustment(item, -removable);
    } catch (err) {
      return setError(err?.message || 'Unable to update the deployment usage history.');
    }

    if (minimum > 0) {
      const next = [...value];
      next[index] = { ...item, quantity: minimum };
      onChange(next);
    } else {
      onChange(value.filter((_, itemIndex) => itemIndex !== index));
    }
    setError('');
  };

  const setUse = next => {
    if (next === false && hasLockedUsage) {
      setError('Supplies were already reported during this deployment. Correct those from My Gear instead of clearing them during return.');
      return;
    }

    if (next === false) {
      try {
        (value || []).forEach(item => {
          const quantity = cleanQuantity(item.quantity);
          if (quantity > 0) recordUsageAdjustment(item, -quantity);
        });
      } catch (err) {
        setError(err?.message || 'Unable to clear the return usage history.');
        return;
      }
      onChange([]);
    }
    onHasUseChange(next);
    setError('');
  };

  const addSomethingElse = () => {
    restockConfirmedRef.current = false;
    setReviewing(false);
    window.setTimeout(() => rootRef.current?.querySelector('.restock-catalog-field input')?.focus(), 0);
  };

  const confirmAndFinishReturn = () => {
    restockConfirmedRef.current = true;
    setReviewing(false);
    const form = rootRef.current?.closest('form');
    window.setTimeout(() => form?.requestSubmit(), 0);
  };

  return (
    <section className="tracking-card restock-used-card" ref={rootRef}>
      <div className="tracking-section-head">
        <div>
          <h3>Medical supplies used?</h3>
          <p>Report what was consumed so the bag can be ordered back to standard. Items already logged from My Gear stay attached to this deployment.</p>
        </div>
      </div>

      <div className="restock-choice-row" role="group" aria-label="Medical supplies used">
        <button type="button" className={hasUse === false ? 'active' : ''} onClick={() => setUse(false)} disabled={hasLockedUsage}>No supplies used</button>
        <button type="button" className={hasUse === true ? 'active' : ''} onClick={() => setUse(true)}>Yes — report supplies</button>
      </div>
      {hasLockedUsage && <p className="restock-help">Usage reported during the deployment is locked into this return list. You can still add anything you forgot.</p>}

      {hasUse === true && (
        <div className="restock-used-body">
          {status === 'loading' && <div className="tracking-empty">Loading the Line Medic inventory catalog…</div>}
          {error && <div className="tracking-error">{error}</div>}
          {status === 'error' && <button type="button" className="tracking-link-btn" onClick={() => setRetryKey(current => current + 1)}>Try catalog again</button>}

          {value.length > 0 && (
            <div className="restock-used-list">
              {value.map((item, index) => {
                const minimum = lockedQuantity(item);
                return (
                  <div className="restock-used-row" key={`${item.deploymentId}-${item.assetId}-${item.catalogId}-${index}`}>
                    <div>
                      <strong>{item.item}</strong>
                      <small>{item.assetName || item.assetId} · Qty {item.quantity}{minimum > 0 ? ` · ${minimum} reported during deployment` : ''}</small>
                    </div>
                    <button type="button" onClick={() => removeItem(index)} disabled={cleanQuantity(item.quantity) <= minimum}>{minimum > 0 ? 'Remove added' : 'Remove'}</button>
                  </div>
                );
              })}
            </div>
          )}

          {status === 'ready' && (
            <div className="restock-entry-grid">
              <label>Bag / container
                <select value={draft.assetId} onChange={event => setDraft(current => ({ ...current, assetId: event.target.value }))}>
                  {(selectedAssets || []).map(asset => <option key={asset.id} value={asset.id}>{asset.name} — {asset.id}</option>)}
                </select>
              </label>

              <div className="restock-catalog-field">
                <label>Start typing item
                  <input
                    value={draft.query}
                    onChange={event => setDraft(current => ({ ...current, query: event.target.value, catalogId: '' }))}
                    placeholder="Adenosine, IV catheter, 4x4…"
                    autoComplete="off"
                  />
                </label>
                {matches.length > 0 && (
                  <div className="restock-catalog-results" role="listbox">
                    {matches.map(item => (
                      <button type="button" key={item.catalogId} onPointerDown={event => { event.preventDefault(); chooseCatalog(item); }}>
                        <strong>{item.item}</strong>
                        <small>{item.component || 'Other'} · {item.catalogId}</small>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <label>Quantity used
                <input value={draft.quantity} onChange={event => setDraft(current => ({ ...current, quantity: event.target.value }))} inputMode="numeric" />
              </label>

              <button type="button" className="tracking-btn tracking-btn-secondary restock-add-item" onClick={addItem}>Add used item</button>
            </div>
          )}

          <p className="restock-help">Submitting the return creates one Restock Request per deployment. The used bag stays <strong>Needs Restock</strong> until it is physically replenished.</p>
        </div>
      )}

      {reviewing && (
        <div className="restock-final-review" role="alertdialog" aria-live="polite">
          <span className="restock-final-kicker">FINAL RESTOCK CHECK</span>
          <h3>This is what will be ordered</h3>
          <p>Confirm the list before the bag is returned.</p>
          <div className="restock-final-list">
            {(value || []).map((item, index) => (
              <div className="restock-final-row" key={`review-${item.deploymentId}-${item.assetId}-${item.catalogId}-${index}`}>
                <div><strong>{item.item}</strong><small>{item.assetName || item.assetId}</small></div>
                <strong>Qty {item.quantity}</strong>
              </div>
            ))}
          </div>
          <h4>Anything else needs to be added?</h4>
          <div className="restock-final-actions">
            <button type="button" className="tracking-btn tracking-btn-secondary" onClick={addSomethingElse}>Yes — add something else</button>
            <button type="button" className="tracking-btn tracking-btn-primary" onClick={confirmAndFinishReturn}>No — list is complete</button>
          </div>
        </div>
      )}
    </section>
  );
}
